import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import './CustomPageLoading.css';

/**
 * 🎨 Custom Page Loading Component
 * 
 * Zeigt Custom Logo und Branding statt BonusClub-Branding
 * Lädt Logo-Daten aus Firestore oder RTDB
 */
function CustomPageLoading({ pageData, spaceInfo, source = 'unknown', customDomainSpaceId = null }) {
    const [logoUrl, setLogoUrl] = useState(null);
    const [siteName, setSiteName] = useState('');
    const [logoSettings, setLogoSettings] = useState({
        width: 120,
        height: 60,
        showLogo: true
    });
    const [spaceBranding, setSpaceBranding] = useState(null);

    useEffect(() => {
        loadCustomBranding();
    }, [pageData, spaceInfo]);

    // Load public space branding (no auth required) - for custom domains
    const loadPublicSpaceBranding = async (spaceId) => {
        try {
            const db = getDatabase();
            
            // Try publicSpaces first (contains custom meta + branding)
            const publicSpaceRef = ref(db, `/publicSpaces/${spaceId}`);
            const publicSpaceSnapshot = await get(publicSpaceRef);
            
            if (publicSpaceSnapshot.exists()) {
                const publicSpaceData = publicSpaceSnapshot.val();
                return {
                    logo: publicSpaceData.customLogo,
                    siteName: publicSpaceData.siteName || publicSpaceData.spaceName,
                    logoWidth: publicSpaceData.customLogoWidth || 120,
                    logoHeight: publicSpaceData.customLogoHeight || 60,
                    showLogo: publicSpaceData.showCustomLogo !== false
                };
            }
            
            // Fallback: Try spacenames for basic info
            const spacenamesRef = ref(db, `/spacenames/${spaceId}`);
            const spacenamesSnapshot = await get(spacenamesRef);
            
            if (spacenamesSnapshot.exists()) {
                const spacenamesData = spacenamesSnapshot.val();
                return {
                    logo: null, // No logo in spacenames
                    siteName: spacenamesData.spaceName || spaceId,
                    logoWidth: 120,
                    logoHeight: 60,
                    showLogo: false // No logo available
                };
            }
            
            console.log('⚠️ CustomPageLoading: No public space branding found for:', spaceId);
        } catch (error) {
            console.error('❌ Error loading public space branding:', error);
        }
        return null;
    };

    // Load space branding directly from RTDB if not available in Firestore
    const loadSpaceBrandingFromRTDB = async (userId, spaceId) => {
        try {
            // 🔥 FIX: Check if user is authenticated before accessing protected data
            const auth = getAuth();
            if (!auth.currentUser) {
                console.log('⚠️ CustomPageLoading: User not authenticated, skipping RTDB space branding');
                return null;
            }
            
            const db = getDatabase();
            const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
            const spaceSnapshot = await get(spaceRef);
            
            if (spaceSnapshot.exists()) {
                const spaceData = spaceSnapshot.val();
                return {
                    customLogo: spaceData.customLogo,
                    customLogoWidth: spaceData.customLogoWidth,
                    customLogoHeight: spaceData.customLogoHeight,
                    showCustomLogo: spaceData.showCustomLogo,
                    siteName: spaceData.siteName || spaceData.spaceName
                };
            }
        } catch (error) {
            console.error('❌ Error loading space branding from RTDB:', error);
        }
        return null;
    };

    const loadCustomBranding = async () => {
        console.log('🎨 CustomPageLoading: Loading custom branding:', { 
            pageData: pageData ? 'present' : 'missing', 
            spaceInfo: spaceInfo ? 'present' : 'missing', 
            source,
            pageDataKeys: pageData ? Object.keys(pageData) : [],
            spaceInfoKeys: spaceInfo ? Object.keys(spaceInfo) : []
        });
        
        let brandingInfo = {};
        
        // 🔥 NEW: For custom domains, try to detect spaceId from URL and load public branding
        const originalHost = window.__BC_ORIGINAL_HOST__ || 
                            new URLSearchParams(window.location.search).get('__bc_original_host');
        
        // 🔥 NEW: For custom domains, try to load public branding
        if (originalHost && !pageData && !spaceInfo) {
            console.log('🌐 CustomPageLoading: Custom domain detected without pageData, trying to load branding...');
            
            let spaceId = null;
            
            // Method 1: Use provided customDomainSpaceId (from PagePublic)
            if (customDomainSpaceId) {
                spaceId = customDomainSpaceId;
                console.log('🔍 CustomPageLoading: Using provided customDomainSpaceId:', spaceId);
            }
            // Method 2: Check window cache (set by PagePublic after KV resolution)
            else if (window.__BC_RESOLVED_SPACE_ID__) {
                spaceId = window.__BC_RESOLVED_SPACE_ID__;
                console.log('🔍 CustomPageLoading: Using cached spaceId from window:', spaceId);
            }
            // Method 3: For /to/username/ routes, extract username as spaceId
            else {
                const currentPath = window.location.pathname;
                const pathParts = currentPath.split('/').filter(part => part);
                
                if (pathParts[0] === 'to' && pathParts[1]) {
                    spaceId = pathParts[1];
                    console.log('🔍 CustomPageLoading: Extracted spaceId from /to/ path:', spaceId);
                }
                // No hardcoded fallbacks! Domain mapping must be resolved dynamically
                else {
                    console.warn('⚠️ CustomPageLoading: Could not determine spaceId for custom domain', {
                        originalHost,
                        currentPath,
                        note: 'This is expected on first load before KV mapping is resolved'
                    });
                }
            }
            
            if (spaceId) {
                console.log('🔍 CustomPageLoading: Attempting to load public branding for spaceId:', spaceId);
                const publicBrandingData = await loadPublicSpaceBranding(spaceId);
                if (publicBrandingData) {
                    brandingInfo = publicBrandingData;
                    console.log('🎨 CustomPageLoading: Using public space branding:', brandingInfo);
                } else {
                    console.log('⚠️ CustomPageLoading: No public branding found for spaceId:', spaceId);
                }
            } else {
                console.log('⚠️ CustomPageLoading: Could not determine spaceId for custom domain');
            }
        }
        
        // 1. Try Firestore siteInfo first (most reliable)
        if (!brandingInfo.logo && source?.includes('firestore') && spaceInfo) {
            brandingInfo = {
                logo: spaceInfo.customLogo,
                siteName: spaceInfo.siteName || spaceInfo.spaceName || spaceInfo.name,
                logoWidth: spaceInfo.customLogoWidth,
                logoHeight: spaceInfo.customLogoHeight,
                showLogo: spaceInfo.showCustomLogo
            };
            console.log('🎨 CustomPageLoading: Using Firestore branding:', brandingInfo);
        }
        
        // 2. If no logo found, try to load from pageData itself (embedded branding)
        if (!brandingInfo.logo && pageData) {
            console.log('🔍 CustomPageLoading: Checking pageData for embedded branding...');
            
            // Check if branding data is embedded in pageData
            // 🎯 PRIORITY: space.customLogo (from Space Settings) > customLogo > livePageImg > navigation.logo
            const embeddedBranding = {
                logo: pageData.space?.customLogo || pageData.customLogo || pageData.livePageImg || pageData.navigation?.logo,
                siteName: pageData.siteName || pageData.name || pageData.spaceName,
                logoWidth: pageData.space?.customLogoWidth || pageData.customLogoWidth || pageData.navigation?.logoWidth,
                logoHeight: pageData.space?.customLogoHeight || pageData.customLogoHeight || pageData.navigation?.logoHeight,
                showLogo: pageData.space?.showCustomLogo !== undefined ? pageData.space.showCustomLogo : (pageData.showCustomLogo !== false) // Default to true
            };
            
            if (embeddedBranding.logo || embeddedBranding.siteName) {
                brandingInfo = embeddedBranding;
                console.log('🎨 CustomPageLoading: Using embedded page branding:', brandingInfo);
            } else {
                // 3. Try public space branding for custom domains (no auth required)
                if (pageData?.spaceId || pageData?.username) {
                    const spaceId = pageData.spaceId || pageData.username;
                    console.log('🔍 CustomPageLoading: Loading public space branding for custom domain...');
                    const publicBrandingData = await loadPublicSpaceBranding(spaceId);
                    if (publicBrandingData) {
                        brandingInfo = publicBrandingData;
                        console.log('🎨 CustomPageLoading: Using public space branding:', brandingInfo);
                    }
                }
                
                // 4. Try RTDB only if user is authenticated (fallback)
                if (!brandingInfo.logo && pageData?.userId && pageData?.spaceId) {
                    console.log('🔍 CustomPageLoading: Loading space branding from RTDB...');
                    const spaceBrandingData = await loadSpaceBrandingFromRTDB(pageData.userId, pageData.spaceId);
                    if (spaceBrandingData) {
                        brandingInfo = {
                            logo: spaceBrandingData.customLogo,
                            siteName: spaceBrandingData.siteName,
                            logoWidth: spaceBrandingData.customLogoWidth,
                            logoHeight: spaceBrandingData.customLogoHeight,
                            showLogo: spaceBrandingData.showCustomLogo
                        };
                        console.log('🎨 CustomPageLoading: Using RTDB space branding:', brandingInfo);
                    }
                }
            }
        }
        
        // 3. Fallback to embedded space data in pageData
        if (!brandingInfo.logo && pageData?.space) {
            // 🔥 FIX: Also check navigation.logo if space.customLogo is missing
            brandingInfo = {
                logo: pageData.space.customLogo || pageData.navigation?.logo,
                siteName: pageData.space.siteName || pageData.space.spaceName,
                logoWidth: pageData.space.customLogoWidth || pageData.navigation?.logoWidth,
                logoHeight: pageData.space.customLogoHeight || pageData.navigation?.logoHeight,
                showLogo: pageData.space.showCustomLogo
            };
            console.log('🎨 CustomPageLoading: Using embedded space branding:', brandingInfo);
        }
        
        // 4. Final fallback to page-level branding (legacy)
        if (!brandingInfo.logo && pageData) {
            // 🎯 PRIORITY: space.customLogo (from Space Settings) > customLogo > livePageImg > navigation.logo
            brandingInfo = {
                logo: pageData.space?.customLogo || pageData.customLogo || pageData.livePageImg || pageData.navigation?.logo,
                siteName: pageData.siteName || pageData.name,
                logoWidth: pageData.space?.customLogoWidth || pageData.customLogoWidth || pageData.navigation?.logoWidth,
                logoHeight: pageData.space?.customLogoHeight || pageData.customLogoHeight || pageData.navigation?.logoHeight,
                showLogo: pageData.space?.showCustomLogo !== undefined ? pageData.space.showCustomLogo : pageData.showCustomLogo
            };
            console.log('🎨 CustomPageLoading: Using page-level branding fallback:', brandingInfo);
        }
        
        // Set branding state
        if (brandingInfo.logo) {
            setLogoUrl(brandingInfo.logo);
        }
        
        if (brandingInfo.siteName) {
            setSiteName(brandingInfo.siteName);
        }
        
        setLogoSettings({
            width: brandingInfo.logoWidth || 120,
            height: brandingInfo.logoHeight || 60,
            showLogo: brandingInfo.showLogo !== false
        });
        
        console.log('✅ CustomPageLoading: Final branding loaded:', brandingInfo);
    };

    // Get theme colors from page data
    const getThemeColor = () => {
        if (pageData?.colors?.color1) return pageData.colors.color1;
        if (pageData?.colors?.color2) return pageData.colors.color2;
        return '#2196F3'; // Default blue
    };

    const getBackgroundColor = () => {
        return pageData?.backgroundColor || '#ffffff';
    };

    console.log('🎨 CustomPageLoading: Rendering decision:', {
        hasLogo: !!logoUrl,
        showLogo: logoSettings.showLogo,
        siteName,
        willShowCustomLogo: logoUrl && logoSettings.showLogo
    });

    // Custom loading with user branding (NO BonusClub!)
    if (logoUrl && logoSettings.showLogo) {
        return (
            <div 
                className="bonusclub-custom-page-loading-container"
                style={{ backgroundColor: '#ffffff' }}
            >
                <div className="bonusclub-custom-loading-content-wrapper">
                    <div className="bonusclub-custom-logo-display-container">
                        <img 
                            src={logoUrl} 
                            alt="Logo"
                            className="bonusclub-custom-loading-logo-image"
                            style={{
                                width: `${logoSettings.width}px`,
                                height: `${logoSettings.height}px`,
                                objectFit: 'contain'
                            }}
                        />
                    </div>
                    
                    <div className="bonusclub-custom-loading-spinner-container">
                        <div 
                            className="bonusclub-custom-loading-spinner-element"
                            style={{ borderTopColor: '#666666' }}
                        ></div>
                    </div>
                    
                </div>
                
            </div>
        );
    }

    // Fallback: Minimal loading without BonusClub branding
    return (
        <div 
            className="bonusclub-minimal-page-loading-container"
            style={{ backgroundColor: getBackgroundColor() }}
        >
            <div className="bonusclub-minimal-loading-content-wrapper">
                <div 
                    className="bonusclub-minimal-loading-spinner-element"
                    style={{ borderTopColor: getThemeColor() }}
                ></div>
            </div>
        </div>
    );
}

export default CustomPageLoading;
